import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Session auth relies exclusively on the HttpOnly cookie set at
// /api/auth/session. `withCredentials: true` ensures the cookie is sent
// on every same-site XHR. We intentionally do NOT read any token from
// client-accessible storage so an XSS foothold cannot exfiltrate it.
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const auth = {
  exchange: (session_id) => apiClient.post("/auth/session", { session_id }),
  me: () => apiClient.get("/auth/me"),
  logout: () => apiClient.post("/auth/logout"),
};

export const plants = {
  identify: (image_base64, save = true, location = null, date_taken = null) =>
    apiClient.post("/plants/identify", {
      image_base64, save,
      ...(location ? { latitude: location.lat, longitude: location.lng } : {}),
      ...(date_taken ? { date_taken } : {}),
    }),
  identifyMulti: (images_base64, save = true, location = null, date_taken = null) =>
    apiClient.post("/plants/identify-multi", {
      images_base64, save,
      ...(location ? { latitude: location.lat, longitude: location.lng } : {}),
      ...(date_taken ? { date_taken } : {}),
    }),
  replacePhoto: (id, image_base64, rerun_identify = true) =>
    apiClient.post(`/plants/${id}/replace-photo`, { image_base64, rerun_identify }),
  list: () => apiClient.get("/plants"),
  get: (id) => apiClient.get(`/plants/${id}`),
  remove: (id) => apiClient.delete(`/plants/${id}`),
  bulkRemove: (scan_ids) => apiClient.post("/plants/bulk-delete", { scan_ids }),
  bulkReview: (scan_ids, reviewed = true) =>
    apiClient.post("/plants/bulk-review", { scan_ids, reviewed }),
  bulkTag: (scan_ids, add_tags = [], remove_tags = []) =>
    apiClient.post("/plants/bulk-tag", { scan_ids, add_tags, remove_tags }),
  rename: (id, common_name) =>
    apiClient.post(`/plants/${id}/rename`, { common_name }),
  retryIdentify: (id) =>
    apiClient.post(`/plants/${id}/retry-identify`),
  saveDraft: (image_base64, location, date_taken = null) =>
    apiClient.post("/plants/draft", {
      image_base64,
      ...(location ? { latitude: location.lat, longitude: location.lng } : {}),
      ...(date_taken ? { date_taken } : {}),
    }),
  identifyExisting: (id) =>
    apiClient.post(`/plants/${id}/identify`),
  duplicates: () =>
    apiClient.get("/plants/duplicates"),
  tags: () =>
    apiClient.get("/plants/tags"),
  nearDuplicates: () =>
    apiClient.get("/plants/near-duplicates"),
  merge: (primary_scan_id, merge_scan_ids, selected_photos = null) =>
    apiClient.post("/plants/merge", {
      primary_scan_id,
      merge_scan_ids,
      ...(selected_photos ? { selected_photos } : {}),
    }),
  undo: (snapshot_id) =>
    apiClient.post("/plants/undo", { snapshot_id }),
  dismissNearDup: (species_key) =>
    apiClient.post("/plants/near-duplicates/dismiss", { species_key }),
  undismissNearDup: (species_key) =>
    apiClient.post("/plants/near-duplicates/undismiss", { species_key }),
  setLocation: (id, location) =>
    apiClient.patch(`/plants/${id}/location`, location || { clear: true }),
  setDateTaken: (id, date_taken) =>
    apiClient.patch(`/plants/${id}/date-taken`, date_taken ? { date_taken } : { clear: true }),
  setTags: (id, tags) =>
    apiClient.patch(`/plants/${id}/tags`, { tags }),
  deleteTag: (tag) => apiClient.delete(`/plants/tags/${encodeURIComponent(tag)}`),
  addPhoto: (id, image_base64) =>
    apiClient.post(`/plants/${id}/photos`, { image_base64 }),
  removePhoto: (id, photo_id) =>
    apiClient.delete(`/plants/${id}/photos/${photo_id}`),
  promotePhoto: (id, photo_id) =>
    apiClient.post(`/plants/${id}/photos/${photo_id}/promote`),
  reorderPhotos: (id, photo_ids) =>
    apiClient.patch(`/plants/${id}/photos/reorder`, { photo_ids }),
  regenerateUses: (id) =>
    apiClient.post(`/plants/${id}/backfill-uses`, {}, { timeout: 90 * 1000 }),
  alternatives: (common_name, scientific_name, plant_type) =>
    apiClient.post("/plants/alternatives", { common_name, scientific_name, plant_type }),
  translate: (id, language) =>
    apiClient.post(`/plants/${id}/translate`, { language }),
  addJournalEntry: (id, { text, entry_date, emoji }) =>
    apiClient.post(`/plants/${id}/journal`, {
      text,
      ...(entry_date ? { entry_date } : {}),
      ...(emoji ? { emoji } : {}),
    }),
  deleteJournalEntry: (id, entry_id) =>
    apiClient.delete(`/plants/${id}/journal/${entry_id}`),
  listJournalEmojis: () => apiClient.get(`/plants/journal/emojis`),
  attachVoiceNote: (id, entry_id, { audio_data_url, duration_ms }) =>
    apiClient.post(`/plants/${id}/journal/${entry_id}/voice`, { audio_data_url, duration_ms }),
  getVoiceNote: (id, entry_id) =>
    apiClient.get(`/plants/${id}/journal/${entry_id}/voice`),
  deleteVoiceNote: (id, entry_id) =>
    apiClient.delete(`/plants/${id}/journal/${entry_id}/voice`),
  exportCsvUrl: () => `${API}/plants/export.csv`,
};

export const geocode = {
  reverse: (lat, lng) => apiClient.get(`/geocode/reverse?lat=${lat}&lng=${lng}`),
  zip: (zip) => apiClient.get(`/geocode/zip?zip=${encodeURIComponent(zip)}`),
};

// Bad-link reporting from the ExternalLinkConfirm dialog. Backend logs
// each report into `link_reports` so operators can rotate stale sources
// out of plantLinks.js.
export const linkReports = {
  submit: ({ url, host, source, scan_id, plant_name, reason }) =>
    apiClient.post("/link-reports", {
      url,
      host: host || "",
      source: source || "",
      scan_id: scan_id || "",
      plant_name: plant_name || "",
      reason: reason || "",
    }),
};

export const profile = {
  states: () => apiClient.get("/profile/states"),
  update: (payload) => apiClient.patch("/profile", payload),
};

// Shared read-only lists — package a subset of scans behind a token link.
export const shares = {
  create: ({ scan_ids, title, view_mode, expires_in_days }) =>
    apiClient.post("/shares", { scan_ids, title, view_mode, expires_in_days }),
  list: () => apiClient.get("/shares"),
  revoke: (share_id) => apiClient.delete(`/shares/${share_id}`),
  // Bulk-cleanup: revoke every share whose title is empty/whitespace.
  // Backend also treats missing `title` field as "untitled" so legacy
  // rows created before the fallback-title change are swept too.
  revokeUntitled: () => apiClient.delete("/shares/untitled"),
  // Public — no auth. Uses a fresh axios instance so we don't attach the Bearer.
  // Accepts optional `{ offset, limit }` for server-side pagination of the
  // items array; the shape stays additive (count/offset/limit/has_more/items).
  publicGet: (token, params) => axios.get(`${API}/public/shares/${token}`, { params }),
};

// Owner-only endpoints — the backend replies 403 for non-owners, and the
// frontend guards the UI itself off `me.isOwner()` first so the request
// never fires in the common case.
export const me = {
  isOwner: () => apiClient.get("/me/is-owner"),
};

export const printCards = {
  // Mint (or reuse) public share tokens for the given scan_ids. Response:
  // { cards: [{ scan_id, share_token }] }. Frontend then composes share URLs
  // using window.location.origin.
  createTokens: (scan_ids) => apiClient.post("/plants/print-cards", { scan_ids }),
};

// Owner-only CSP violation dashboard. The backend gates on OWNER_EMAILS
// so a non-owner will hit 403; the Settings entry uses `me.isOwner()` to
// avoid rendering the link for non-owners in the first place.
export const cspAdmin = {
  summary: (days = 7) => apiClient.get("/csp-report/admin/summary", { params: { days } }),
  recent: ({ limit = 50, before, directive } = {}) =>
    apiClient.get("/csp-report/admin/recent", {
      params: {
        limit,
        ...(before ? { before } : {}),
        ...(directive ? { directive } : {}),
      },
    }),
  listAllowlist: () => apiClient.get("/csp-report/admin/allowlist"),
  addAllowlist: ({ directive, source, note }) =>
    apiClient.post("/csp-report/admin/allowlist", { directive, source, note }),
  removeAllowlist: (entry_id) =>
    apiClient.delete(`/csp-report/admin/allowlist/${entry_id}`),
};


export const milestones = {
  // Full catalog + user's already-unlocked ids/timestamps.
  list: () => apiClient.get("/milestones"),
  // Recompute + persist. Returns `newly_unlocked` (empty when nothing new).
  // Idempotent: safe to call after every plant save / share create.
  check: () => apiClient.post("/milestones/check"),
};
